import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useServiceOS } from '../hooks/useServiceOS';
import { Wrench, ArrowRight, CheckCircle } from '@phosphor-icons/react';

const BUSINESS_TYPES = [
  { value: 'plumbing', label: 'Plumbing' },
  { value: 'electrical', label: 'Electrical' },
  { value: 'hvac', label: 'HVAC' },
  { value: 'cleaning', label: 'Cleaning' },
  { value: 'landscaping', label: 'Landscaping' },
  { value: 'painting', label: 'Painting' },
  { value: 'roofing', label: 'Roofing' },
  { value: 'other', label: 'Other' },
];

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export default function SOSOnboarding() {
  const navigate = useNavigate();
  const { completeOnboarding } = useServiceOS();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    business_name: '',
    business_type: 'plumbing',
    working_days: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
    workday_start_time: '08:00',
    workday_end_time: '17:00',
    default_job_duration_minutes: 60,
    buffer_minutes: 15,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/London',
  });

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const toggleDay = (day) => {
    setForm(prev => ({
      ...prev,
      working_days: prev.working_days.includes(day)
        ? prev.working_days.filter(d => d !== day)
        : [...prev.working_days, day],
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      await completeOnboarding(form);
      navigate('/service-os');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const canProceed = step === 0 ? form.business_name.trim().length > 0 : form.working_days.length > 0;

  return (
    <div className="min-h-screen bg-[#0B0C10] flex items-center justify-center p-4" data-testid="sos-onboarding">
      <div className="w-full max-w-lg">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#14F195]/10 rounded-full text-[#14F195] text-xs font-medium mb-4">
            <Wrench size={14} weight="duotone" /> Service OS Setup
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Set up your business</h1>
          <p className="text-sm text-[#8B8FA3]">Takes less than 2 minutes. You can change everything later.</p>
        </div>

        {/* Progress */}
        <div className="flex gap-2 mb-8">
          {[0, 1].map(i => (
            <div key={i} className={`h-1 flex-1 rounded-full transition-colors ${i <= step ? 'bg-[#14F195]' : 'bg-[#1E1F2E]'}`} />
          ))}
        </div>

        <div className="bg-[#12131A] border border-[#1E1F2E] rounded-xl p-6">
          {step === 0 && (
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-[#C4C9D4] mb-1.5">Business name</label>
                <input
                  type="text"
                  value={form.business_name}
                  onChange={e => set('business_name', e.target.value)}
                  placeholder="e.g. Smith Plumbing Co"
                  className="w-full px-4 py-2.5 bg-[#0B0C10] border border-[#1E1F2E] rounded-lg text-white text-sm focus:outline-none focus:border-[#14F195]/50"
                  data-testid="onboarding-business-name"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#C4C9D4] mb-1.5">Type of business</label>
                <div className="grid grid-cols-2 gap-2">
                  {BUSINESS_TYPES.map(bt => (
                    <button
                      key={bt.value}
                      onClick={() => set('business_type', bt.value)}
                      className={`px-3 py-2 rounded-lg text-sm border transition-colors ${
                        form.business_type === bt.value
                          ? 'border-[#14F195] bg-[#14F195]/10 text-[#14F195]'
                          : 'border-[#1E1F2E] text-[#8B8FA3] hover:border-[#2E2F3E]'
                      }`}
                      data-testid={`onboarding-type-${bt.value}`}
                    >
                      {bt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-[#C4C9D4] mb-1.5">Working days</label>
                <div className="flex gap-2 flex-wrap">
                  {DAYS.map(d => (
                    <button
                      key={d}
                      onClick={() => toggleDay(d)}
                      className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${
                        form.working_days.includes(d)
                          ? 'border-[#14F195] bg-[#14F195]/10 text-[#14F195]'
                          : 'border-[#1E1F2E] text-[#8B8FA3] hover:border-[#2E2F3E]'
                      }`}
                      data-testid={`onboarding-day-${d}`}
                    >
                      {d}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#C4C9D4] mb-1.5">Start time</label>
                  <input
                    type="time"
                    value={form.workday_start_time}
                    onChange={e => set('workday_start_time', e.target.value)}
                    className="w-full px-4 py-2.5 bg-[#0B0C10] border border-[#1E1F2E] rounded-lg text-white text-sm focus:outline-none focus:border-[#14F195]/50"
                    data-testid="onboarding-start-time"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#C4C9D4] mb-1.5">End time</label>
                  <input
                    type="time"
                    value={form.workday_end_time}
                    onChange={e => set('workday_end_time', e.target.value)}
                    className="w-full px-4 py-2.5 bg-[#0B0C10] border border-[#1E1F2E] rounded-lg text-white text-sm focus:outline-none focus:border-[#14F195]/50"
                    data-testid="onboarding-end-time"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#C4C9D4] mb-1.5">Default job duration</label>
                  <select
                    value={form.default_job_duration_minutes}
                    onChange={e => set('default_job_duration_minutes', parseInt(e.target.value))}
                    className="w-full px-4 py-2.5 bg-[#0B0C10] border border-[#1E1F2E] rounded-lg text-white text-sm focus:outline-none focus:border-[#14F195]/50"
                    data-testid="onboarding-duration"
                  >
                    <option value={30}>30 min</option>
                    <option value={60}>1 hour</option>
                    <option value={90}>1.5 hours</option>
                    <option value={120}>2 hours</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#C4C9D4] mb-1.5">Buffer between jobs</label>
                  <select
                    value={form.buffer_minutes}
                    onChange={e => set('buffer_minutes', parseInt(e.target.value))}
                    className="w-full px-4 py-2.5 bg-[#0B0C10] border border-[#1E1F2E] rounded-lg text-white text-sm focus:outline-none focus:border-[#14F195]/50"
                    data-testid="onboarding-buffer"
                  >
                    <option value={0}>None</option>
                    <option value={15}>15 min</option>
                    <option value={30}>30 min</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {error && <p className="mt-3 text-red-400 text-sm" data-testid="onboarding-error">{error}</p>}

          <div className="flex justify-between mt-6">
            {step > 0 ? (
              <button onClick={() => setStep(step - 1)} className="text-sm text-[#8B8FA3] hover:text-white transition-colors">
                Back
              </button>
            ) : <div />}
            {step < 1 ? (
              <button
                onClick={() => setStep(1)}
                disabled={!canProceed}
                className="flex items-center gap-2 px-5 py-2.5 bg-[#14F195] text-[#0B0C10] rounded-lg text-sm font-semibold hover:bg-[#12D984] disabled:opacity-40 transition-colors"
                data-testid="onboarding-next-btn"
              >
                Next <ArrowRight size={16} />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={!canProceed || loading}
                className="flex items-center gap-2 px-5 py-2.5 bg-[#14F195] text-[#0B0C10] rounded-lg text-sm font-semibold hover:bg-[#12D984] disabled:opacity-40 transition-colors"
                data-testid="onboarding-complete-btn"
              >
                {loading ? 'Setting up...' : <><CheckCircle size={16} weight="bold" /> Complete setup</>}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
